import { baseApi } from "./baseApi";
import type { Service, ServiceHealth } from "../api";

type ServiceWithHealth = Service & { health?: ServiceHealth | null };

interface Summary {
  operational: number;
  degraded: number;
  major: number;
  total: number;
  error_rate_global: number;
  requests_last_hour: number;
  p95_latency_global: number;
}

interface CreateServiceBody {
  /** Identificador legible único por org. Reemplaza al `id` text de versiones anteriores. */
  slug: string;
  name: string;
  category: string;
  org_id?: string;
}

/**
 * Endpoints de servicios usando RTK Query. Los hooks `useGetServicesQuery`,
 * `useCreateServiceMutation`, etc. se generan automáticamente y son tipados.
 *
 * Reemplaza el patrón antiguo:
 *   useEffect(() => { apiGet('/v1/services').then(setServices) }, [])
 * por:
 *   const { data, isLoading } = useGetServicesQuery()
 *
 * Y reemplaza los `load()` manuales tras una mutation por `invalidatesTags`:
 * al ejecutar deleteService, todas las queries con tag "Services" se refetchean.
 */
export const servicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServices: builder.query<ServiceWithHealth[], { expand?: "health"; orgId?: string } | void>({
      // `orgId` fuerza el X-Org-Id de ESTA query sin tocar la org global —
      // cada orgId cachea por separado (el arg es parte de la cache key).
      query: (params) => ({
        url: `/v1/services${params?.expand ? `?expand=${params.expand}` : ""}`,
        ...(params?.orgId ? { headers: { "X-Org-Id": params.orgId } } : {}),
      }),
      transformResponse: (response: { services: ServiceWithHealth[] }) => response.services,
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "Services" as const, id: s.id })),
              { type: "Services", id: "LIST" },
            ]
          : [{ type: "Services", id: "LIST" }],
    }),
    getServicesSummary: builder.query<Summary, void>({
      query: () => "/v1/services/summary",
      // Tag compartido con la lista — al crear/eliminar un servicio se refrescan ambos.
      providesTags: [{ type: "Services", id: "LIST" }],
    }),
    createService: builder.mutation<Service, CreateServiceBody>({
      query: (body) => ({ url: "/v1/services", method: "POST", body }),
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),
    deleteService: builder.mutation<void, string>({
      query: (slug) => ({ url: `/v1/services/${encodeURIComponent(slug)}`, method: "DELETE" }),
      // Invalida la lista — todas las páginas que muestran services refrescarán.
      invalidatesTags: (_result, _error, slug) => [
        { type: "Services", id: slug },
        { type: "Services", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetServicesQuery,
  useGetServicesSummaryQuery,
  useCreateServiceMutation,
  useDeleteServiceMutation,
} = servicesApi;

/**
 * Hook helper que devuelve `Record<service_id_uuid, slug>` derivado del cache
 * de RTK Query. Útil para mostrar el slug en chips de eventos/trazas/incidents
 * que solo cargan el uuid.
 *
 * Si la lista todavía no fue fetcheada, devuelve un objeto vacío — los
 * consumidores deben hacer fallback al uuid truncado.
 */
export function useServiceSlugMap(): Record<string, string> {
  const { data } = useGetServicesQuery();
  if (!data) return {};
  const map: Record<string, string> = {};
  for (const s of data) map[s.id] = s.slug;
  return map;
}
